/**
 * CreditorStatementCard · Sprint 3 · v2.8.4
 * Estado de caja consolidado por acreedor, agrupado en 3 categorías:
 *  - project_loan   → crédito del proyecto (puente nuevo)
 *  - dragged        → pasivos arrastrados pre-proyecto
 *  - structured     → deuda estructurada / mezzanine
 */
import { useQuery } from '@tanstack/react-query';
import { analyzerAPI } from '@/lib/api';
import { Landmark, FileWarning, Layers } from 'lucide-react';

const CATEGORY_META = {
  project_loan: { label: 'Crédito del Proyecto', Icon: Landmark, color: '#4F46E5', bg: 'rgba(79,70,229,0.06)' },
  dragged:      { label: 'Pasivos arrastrados',  Icon: FileWarning, color: '#0369A1', bg: 'rgba(3,105,161,0.06)' },
  structured:   { label: 'Deuda estructurada',   Icon: Layers,      color: '#7C3AED', bg: 'rgba(124,58,237,0.06)' },
};

function fmt(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function CreditorStatementCard({ scenarioId }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['creditor-statement', scenarioId],
    queryFn: async () => (await analyzerAPI.creditorStatement(scenarioId)).data,
    enabled: !!scenarioId,
    staleTime: 60000,
  });

  if (isLoading) {
    return <div className="rounded-2xl border border-border/40 bg-card p-6 text-xs text-muted-foreground" data-testid="creditor-card-loading">Cargando estado por acreedor...</div>;
  }
  if (error || !data?.categories) return null;

  const cats = data.categories;
  const hasAny = ['project_loan','dragged','structured'].some(k => {
    const c = cats[k];
    if (!c) return false;
    if (k === 'project_loan') return (c.monthly || []).length > 0;
    return (c.creditors || []).length > 0;
  });
  if (!hasAny) return null;

  return (
    <div className="rounded-2xl border border-border/40 bg-card p-6" data-testid="creditor-statement-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: '#1E293B' }}>Estado por acreedor · consolidado</h3>
          <p className="text-[11px] mt-1" style={{ color: '#64748B' }}>
            Reporte segmentado por taxonomía formal (Sprint 3 · motor v{data.engine_version}). Cada acreedor
            con sus totales de proceeds, intereses pagados y principal amortizado.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {['project_loan', 'dragged', 'structured'].map(key => {
          const cat = cats[key];
          const meta = CATEGORY_META[key];
          if (!cat) return null;
          const isProjectLoan = key === 'project_loan';
          const creditors = isProjectLoan ? [{ label: 'Crédito del Proyecto', totals: cat.totals, monthly: cat.monthly }] : (cat.creditors || []);
          const totals = cat.totals || {};
          const totalPrincipal = totals.principal_paid || 0;
          const totalProceeds = totals.proceeds || 0;
          const totalInterest = totals.interest_paid || 0;
          const activeCount = creditors.filter(c => (c.monthly || []).length > 0).length;

          return (
            <div
              key={key}
              className="rounded-xl border p-4"
              style={{ borderColor: activeCount > 0 ? meta.color : 'rgba(148,163,184,0.3)', background: activeCount > 0 ? meta.bg : 'transparent' }}
              data-testid={`creditor-cat-${key}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <meta.Icon className="w-4 h-4" style={{ color: meta.color }} />
                <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: meta.color }}>
                  {meta.label}
                </div>
              </div>

              {activeCount === 0 ? (
                <p className="text-[10px]" style={{ color: '#94A3B8' }}>Sin acreedores en este proyecto.</p>
              ) : (
                <>
                  <div className="space-y-2 mb-3">
                    {creditors.map((c, idx) => {
                      const isActive = (c.monthly || []).length > 0;
                      if (!isActive) return null;
                      return (
                        <div key={c.creditor_id || `pl-${idx}`} className="text-[11px] border-l-2 pl-2 py-0.5" style={{ borderColor: meta.color }}>
                          <div className="font-medium truncate" style={{ color: '#1E293B' }} title={c.label}>{c.label}</div>
                          <div className="grid grid-cols-2 gap-x-2 mt-0.5" style={{ color: '#64748B' }}>
                            <span>Proceeds: <strong style={{ color: '#334155' }}>{fmt(c.totals?.proceeds)}</strong></span>
                            <span>Interés: <strong style={{ color: '#334155' }}>{fmt(c.totals?.interest_paid)}</strong></span>
                            <span>Principal: <strong style={{ color: '#334155' }}>{fmt(c.totals?.principal_paid)}</strong></span>
                            <span>Meses: <strong style={{ color: '#334155' }}>{(c.monthly || []).length}</strong></span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t pt-2" style={{ borderColor: 'rgba(148,163,184,0.25)' }}>
                    <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: '#94A3B8' }}>Totales categoría</div>
                    <div className="grid grid-cols-2 gap-x-2 text-[11px]">
                      <span style={{ color: '#64748B' }}>Interés pagado: <strong style={{ color: meta.color }}>{fmt(totalInterest)}</strong></span>
                      <span style={{ color: '#64748B' }}>Principal: <strong style={{ color: meta.color }}>{fmt(totalPrincipal)}</strong></span>
                      <span style={{ color: '#64748B' }}>Proceeds: <strong style={{ color: meta.color }}>{fmt(totalProceeds)}</strong></span>
                      {!isProjectLoan && <span style={{ color: '#64748B' }}>Acreedores: <strong style={{ color: meta.color }}>{totals.creditor_count || 0}</strong></span>}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
